/**
 * ノート編集画面（docs/01-requirements.md §5.1〜§5.3, §5.9）。
 *
 * この画面が守っている規律（CLAUDE.md 絶対規則）:
 * 1. **保存ロジックを持たない。** 状態機械は `useNoteEditor` にある。ここは表示だけ。
 * 2. **IME**: エディタの adapter を `bindComposing` へ登録し、composition 中はサーバー
 *    送信が止まるようにする。タイトル入力欄の composition も同じ判定に含める
 *    （変換途中のタイトルを確定本文として送らないため）。
 * 3. **書き始めの摩擦をゼロにする。** 新規作成時は本文へ即フォーカスし、種類・タイトル・
 *    カテゴリの選択を要求しない。タイトルは空のままでよいことがプレースホルダで分かる。
 * 4. **エディタを再描画しない。** 保存ステータスは独立コンポーネント、本文は
 *    `key`（ルート由来）を変えたときだけ再マウントする。ステータス更新やプレビュー更新で
 *    Editor の DOM は触られない（Editor は memo で完全に隔離されている）。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { errorMessage, ErrorNotice } from '@app/components/ErrorNotice';
import { MarkdownView } from '@app/components/MarkdownView';
import { SaveStatusChip } from '@app/components/SaveStatusChip';
import { useCategories } from '@app/components/useCategories';
import { Icon } from '@app/components/icons';
import {
  Editor,
  insertImageReference,
  type EditorAdapter,
  type EditorSwitcher,
} from '@app/editor';
import { imageFilesFromClipboard, prepareImageForUpload } from '@app/images/resize';
import { isLocalNoteId } from '@app/storage/draft-buffer';
import { SESSION_ID } from '@app/storage/session-id';
import { useNoteEditor, type NoteEditorHandle } from '@app/save/useNoteEditor';
import { useToast } from '@app/ui/toast';
import { normalizeSubjectRange, todayJst, yesterdayJst } from '@shared/date';
import type { JstDate } from '@shared/types';
import { resolveBackNavigation } from '@app/views/back-navigation';

export function NotePage() {
  const params = useParams<{ noteId?: string }>();
  const routeNoteId = params.noteId;
  const handle = useNoteEditor(routeNoteId);

  if (handle.load.kind === 'loading') {
    return <div className="centered-message">読み込み中…</div>;
  }

  if (handle.load.kind === 'not_found') {
    return (
      <div className="centered-message">
        <p>このノートは見つかりませんでした。</p>
        <Link className="al-button al-button--primary" to="/">
          タイムラインへ
        </Link>
      </div>
    );
  }

  if (handle.load.kind === 'error') {
    return (
      <div className="centered-message">
        <p>ノートを開けませんでした。</p>
        <p className="muted">{handle.load.message}</p>
        <Link className="al-button al-button--primary" to="/">
          タイムラインへ
        </Link>
      </div>
    );
  }

  // 別のノートへ切り替えたら本文を作り直す。
  // key に `handle.noteId` を使わないのは、新規ノートの初回保存でサーバー ID が
  // 割り当てられた瞬間にエディタが再マウントされ、入力中のフォーカスと undo 履歴が
  // 失われるため。ルート上の ID を使えば、切り替えのときだけ再マウントされる。
  return <NoteEditorView key={routeNoteId ?? 'new'} handle={handle} isNew={routeNoteId == null} />;
}

interface NoteEditorViewProps {
  handle: NoteEditorHandle;
  isNew: boolean;
}

function NoteEditorView({ handle, isNew }: NoteEditorViewProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const categories = useCategories();

  // 最新の handle をコールバックから読むための ref。
  // Editor は memo で隔離されているので、渡す props は初回のものが使われ続ける。
  const handleRef = useRef(handle);
  handleRef.current = handle;

  const adapterRef = useRef<EditorAdapter | null>(null);
  const compositionUnsubRef = useRef<(() => void) | null>(null);
  /** タイトル入力欄が変換中か。エディタの composition と OR で判定する。 */
  const titleComposingRef = useRef(false);
  const bodyRef = useRef(handle.initialContent.body);

  const [categoryId, setCategoryId] = useState<number | null>(handle.initialContent.category_id);
  const [start, setStart] = useState<string>(handle.initialContent.subject_date);
  const [end, setEnd] = useState<string>(handle.initialContent.subject_date_end ?? '');
  const [rangeOpen, setRangeOpen] = useState(handle.initialContent.subject_date_end != null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const [previewOn, setPreviewOn] = useState(false);
  const [previewSource, setPreviewSource] = useState(handle.initialContent.body);

  /**
   * エディタ切り替え（CM6 / textarea）の材料。`Editor` からマウント時と切り替え後に渡る。
   * ここに置くのは、切り替えボタンをヘッダへ出すため（表示範囲を最小にする）。
   * **この state が変わっても `Editor` は再描画されない**（memo の比較関数が常に true）。
   */
  const [editorSwitcher, setEditorSwitcher] = useState<EditorSwitcher | null>(null);

  const previewOnRef = useRef(previewOn);
  previewOnRef.current = previewOn;
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** プレビューの更新。変換中は更新しない（確定後に改めて反映する）。 */
  const schedulePreview = useCallback(() => {
    if (previewTimerRef.current) return;
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      if (adapterRef.current?.isComposing()) return;
      setPreviewSource(bodyRef.current);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, []);

  // --- エディタ接続 -------------------------------------------------------

  const handleEditorReady = useCallback(
    (adapter: EditorAdapter | null) => {
      compositionUnsubRef.current?.();
      compositionUnsubRef.current = null;
      adapterRef.current = adapter;

      if (adapter) {
        // 絶対規則1: composition 中はサーバーへ送らせない。
        handleRef.current.bindComposing(
          () => adapter.isComposing() || titleComposingRef.current,
        );
        compositionUnsubRef.current = adapter.onCompositionEnd(() => {
          handleRef.current.notifyCompositionEnd();
          if (previewOnRef.current) schedulePreview();
        });
      } else {
        handleRef.current.bindComposing(() => titleComposingRef.current);
      }
    },
    [schedulePreview],
  );

  const handleBodyChange = useCallback(
    (value: string) => {
      bodyRef.current = value;
      handleRef.current.updateBody(value);
      if (previewOnRef.current) schedulePreview();
    },
    [schedulePreview],
  );

  // --- 画像 ---------------------------------------------------------------

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadAndInsert = useCallback(
    async (file: File) => {
      setUploading((n) => n + 1);
      try {
        const prepared = await prepareImageForUpload(file);
        const upload = new File([prepared.blob], prepared.filename, { type: prepared.blob.type });
        // 新規ノートはまだサーバー上に無いことがある。その場合 note_id は後付けされない
        // ため null で送る（画像自体は残り、本文からは URL で参照できる）。
        const meta = await api.uploadImage(upload, handleRef.current.note?.id ?? null);
        const inserted = insertImageReference(adapterRef.current, meta.url, '');
        if (!inserted) {
          toast.show('変換の確定後にもう一度お試しください（画像は保存済みです）');
        }
      } catch (error: unknown) {
        toast.show(`画像を追加できませんでした: ${errorMessage(error)}`);
      } finally {
        setUploading((n) => n - 1);
      }
    },
    [toast],
  );

  // --- 切り出し（docs/01-requirements.md §5.11） ---------------------------

  const [extracting, setExtracting] = useState(false);

  const onRequestExtract = useCallback(
    (selection: { start: number; end: number } | null) => {
      if (!selection) {
        toast.show('切り出したい範囲を選択してから押してください');
        return;
      }
      void (async () => {
        setExtracting(true);
        try {
          // **順序が重要。** 選択範囲の位置はこの端末の本文に対するもの。サーバー側の
          // 本文が古いままだと、まったく別の位置が切り出される。先に保存を確定させる。
          await handleRef.current.flush();
          // **await のあとに ref を読み直す。** 新規ノートは flush の中で初めてサーバー
          // ID が確定するため、await の前に捕まえた handle を使うと `local:` のままに
          // 見えて、初回だけ必ず弾かれる（監査 A-6）。
          const handleNow = handleRef.current;
          const noteId = handleNow.note?.id ?? handleNow.noteId;
          if (isLocalNoteId(noteId)) {
            toast.show('保存が完了してからもう一度お試しください');
            return;
          }

          const result = await api.extractNote(noteId, {
            selection_start: selection.start,
            selection_end: selection.end,
            session_id: SESSION_ID,
          });

          // 本文がサーバー側で書き換わった。古い内容を送り返さないよう、
          // 保留中の保存を捨ててから読み直す。
          await handleNow.reloadAfterServerEdit();

          if (isNew) {
            // `/notes/new` のままだと、読み直しがルート上は「新規ノート」として走る。
            // 新規ノートの読み込みには await が無いため React が loading を描画せず、
            // 編集画面が再マウントされない。結果、**切り出し前の本文が画面に残る**。
            // 初回保存で `history.replaceState` した URL はルーターに伝わっていないので、
            // ここで実際に遷移してルート上の ID を確定させる（履歴は増やさない）。
            navigate(`/notes/${noteId}/edit`, { replace: true });
          }

          toast.show('新しいノートへ切り出しました', {
            label: '開く',
            run: () => navigate(`/notes/${result.new_note.id}/edit`),
          });
          // 取り消しは「元ノートを直前のリビジョンへ戻す」＋「新ノートをゴミ箱へ」。
          // 移動とリンク挿入を一体で取り消す（§5.11）。
          toast.show('元に戻せます', {
            label: '元に戻す',
            run: () => {
              void (async () => {
                try {
                  await api.revertNote(noteId, result.undo_revision_id);
                  await api.trashNote(result.new_note.id);
                  await handleRef.current.reloadAfterServerEdit();
                  toast.show('切り出しを取り消しました');
                } catch (error: unknown) {
                  toast.show(`取り消せませんでした: ${errorMessage(error)}`);
                }
              })();
            },
          });
        } catch (error: unknown) {
          toast.show(`切り出せませんでした: ${errorMessage(error)}`);
        } finally {
          setExtracting(false);
        }
      })();
    },
    // isNew はマウント時に固定される（ルート由来）。依存に入れても再生成されない。
    [isNew, navigate, toast],
  );

  const onRequestImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // クリップボードからの貼り付け（PC・スマホ共通の動線。§5.8）。
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const onPaste = (event: ClipboardEvent) => {
      const files = imageFilesFromClipboard(event);
      if (files.length === 0) return;
      event.preventDefault();
      for (const file of files) void uploadAndInsert(file);
    };
    // capture 段階で拾う。エディタ実装（CM6）が自前の貼り付け処理を行う前に横取りする。
    surface.addEventListener('paste', onPaste, true);
    return () => surface.removeEventListener('paste', onPaste, true);
  }, [uploadAndInsert]);

  // --- 対象日 -------------------------------------------------------------

  const applySubject = useCallback(
    (nextStart: string, nextEnd: string) => {
      setStart(nextStart);
      setEnd(nextEnd);
      try {
        const normalized = normalizeSubjectRange(nextStart as JstDate, nextEnd || null);
        setDateError(null);
        handleRef.current.update({
          subject_date: normalized.subject_date,
          subject_date_end: normalized.subject_date_end,
        });
      } catch (error: unknown) {
        // 不正な範囲は保存しない。入力欄はそのまま残して直せるようにする。
        setDateError(errorMessage(error));
      }
    },
    [],
  );

  const setToday = useCallback(() => {
    setRangeOpen(false);
    applySubject(todayJst(), '');
  }, [applySubject]);

  const setYesterday = useCallback(() => {
    setRangeOpen(false);
    applySubject(yesterdayJst(), '');
  }, [applySubject]);

  // --- その他の操作 -------------------------------------------------------

  const onTrash = useCallback(() => {
    void (async () => {
      const noteId = handleRef.current.note?.id;
      if (!noteId) {
        toast.show('まだ保存が終わっていません。少し待ってからもう一度お試しください');
        return;
      }
      if (!window.confirm('このノートをゴミ箱へ移動しますか？（ゴミ箱から復元できます）')) return;
      try {
        await handleRef.current.flush();
        await api.trashNote(noteId);
        navigate('/');
      } catch (error: unknown) {
        toast.show(`ゴミ箱へ移動できませんでした: ${errorMessage(error)}`);
      }
    })();
  }, [navigate, toast]);

  const flushBeforeLeave = useCallback(() => handleRef.current.flush(), []);

  const noteId = handle.note?.id ?? null;
  // 直リンクの既存編集は閲覧へ置換し、その閲覧からは起動画面へ戻す。これにより
  // 編集 ↔ 閲覧の戻るループを作らない。既存ノートも autoFocus はしない（§5.18）。
  const back = resolveBackNavigation(
    location.key,
    location.state,
    noteId ? `/notes/${noteId}` : '/',
    noteId ? '/' : undefined,
  );

  return (
    <div className={previewOn ? 'page note-page note-page--wide' : 'page note-page'}>
      {/* 保存状態のとなりに、このノートに対する操作（履歴・ゴミ箱）とエディタ切り替えを並べる。
          いずれもアイコン 1 つ分に抑え、本文の領域を削らない。 */}
      <AppHeader
        title={isNew && !noteId ? '新しいノート' : 'ノート'}
        backTo={back.to}
        backReplace={back.replace}
        backState={back.state}
        onBeforeBack={flushBeforeLeave}
        right={
          <>
            <SaveStatusChip status={handle.status} onRetry={handle.retryNow} />
            <button
              type="button"
              className="icon-button pc-only"
              aria-pressed={previewOn}
              aria-label="プレビュー"
              title="プレビュー"
              onClick={() => {
                if (!previewOn) setPreviewSource(bodyRef.current);
                setPreviewOn((on) => !on);
              }}
            >
              <Icon name="preview" />
            </button>
            {editorSwitcher && (
              <button
                type="button"
                className="icon-button"
                aria-label={
                  editorSwitcher.kind === 'codemirror'
                    ? 'エディタ: CodeMirror（押すと textarea へ切り替え）'
                    : 'エディタ: textarea（押すと CodeMirror へ切り替え）'
                }
                title={editorSwitcher.kind === 'codemirror' ? 'CodeMirror' : 'textarea'}
                // IME 規律: フォーカスを奪わない（変換の中断と誤爆を防ぐ）。
                onPointerDown={(ev) => ev.preventDefault()}
                onClick={editorSwitcher.toggle}
              >
                <Icon
                  name={editorSwitcher.kind === 'codemirror' ? 'editor-code' : 'editor-plain'}
                />
              </button>
            )}
            {noteId && (
              <Link
                className="icon-button"
                to={`/notes/${noteId}/revisions`}
                aria-label="履歴"
                title="履歴"
              >
                <Icon name="history" />
              </Link>
            )}
            <button
              type="button"
              className="icon-button"
              onClick={onTrash}
              aria-label="ゴミ箱へ移動"
              title="ゴミ箱へ移動"
            >
              <Icon name="trash" />
            </button>
          </>
        }
      />

      {handle.restoredFromDraft && (
        <p className="al-notice al-notice--cream notice-block">
          この端末に残っていた未送信の内容を復元しました。
        </p>
      )}

      <div className="note-page__meta">
        {/* タイトル・カテゴリ・対象日は、いずれも**面に溶け込む入力欄**（`--seamless`）。
            編集画面は書いた内容そのものが画面なので、入力欄の箱が本文より強く見えないようにする
            （ADR-034 / design-system/README.md §4）。 */}
        <div className="note-page__row note-page__row--title">
          <input
            className="al-input al-input--seamless note-page__title"
            type="text"
            defaultValue={handle.initialContent.title ?? ''}
            placeholder="タイトル"
            aria-label="タイトル（任意。空のままでかまいません）"
            onCompositionStart={() => {
              titleComposingRef.current = true;
            }}
            onCompositionEnd={() => {
              titleComposingRef.current = false;
              handleRef.current.notifyCompositionEnd();
            }}
            onChange={(event) => {
              const value = event.target.value;
              handleRef.current.update({ title: value === '' ? null : value });
            }}
          />

          {/* カテゴリはタイトルの右。行の高さを変えない要素なので、独立した行を作らない。
              語彙を増やす導線はここに置かない（docs/01 §4.3 の意図的な摩擦）。 */}
          <select
            className={
              categoryId != null
                ? 'al-select al-select--seamless note-page__category note-page__category--set'
                : 'al-select al-select--seamless note-page__category'
            }
            aria-label="カテゴリ"
            title="カテゴリ"
            value={categoryId ?? ''}
            onChange={(event) => {
              const value = event.target.value === '' ? null : Number(event.target.value);
              setCategoryId(value);
              handleRef.current.update({ category_id: value });
            }}
          >
            {/* 未分類は正当な状態（docs/01 §4.1）。ここだけを見ても何の欄か分かるよう、
                「なし」ではなく語を添える（DESIGN-SYSTEM.md §9）。 */}
            <option value="">カテゴリなし</option>
            {categories.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* 対象日は単独の行。**期間を開いても行が増えない**ようにするため
            （開閉のたびにレイアウトが変わるのがノイズになる）。
            左＝いま指定されている日付、右＝日付を変える操作、と役割で分ける。

            **期間を開いている間は「昨日」「今日」を出さない**（ADR-037 の追記）。
            この 2 つは単日へ設定するショートカットで、期間の始まりと終わりの
            どちらを動かすのかが決まらない。押した先が曖昧な操作を出しておかない。
            幅の面でも、日付欄 2 つを省略なしで出すには右側に 44px 1 つぶんしか残らない。 */}
        <div className="note-page__row note-page__row--date">
          <input
            className="al-input al-input--seamless note-page__date"
            type="date"
            value={start}
            aria-label="対象日"
            onChange={(event) => applySubject(event.target.value, end)}
          />
          {rangeOpen && (
            <>
              <span className="note-page__date-sep" aria-hidden="true">
                〜
              </span>
              <input
                className="al-input al-input--seamless note-page__date"
                type="date"
                value={end}
                aria-label="対象期間の終了日"
                onChange={(event) => applySubject(start, event.target.value)}
              />
            </>
          )}

          <div className="note-page__date-actions">
            <button
              type="button"
              className="icon-button"
              aria-pressed={rangeOpen}
              aria-label="期間指定"
              title="期間指定"
              onClick={() => {
                const next = !rangeOpen;
                setRangeOpen(next);
                // 期間指定を閉じたら単日へ戻す。
                if (!next) applySubject(start, '');
              }}
            >
              <Icon name="range" />
            </button>
            {!rangeOpen && (
              <>
                <button
                  type="button"
                  className="al-button al-button--secondary date-controls__quick"
                  onClick={setYesterday}
                >
                  昨日
                </button>
                <button
                  type="button"
                  className="al-button al-button--secondary date-controls__quick"
                  onClick={setToday}
                >
                  今日
                </button>
              </>
            )}
          </div>
        </div>

        {dateError && <p className="al-notice al-notice--danger">{dateError}</p>}
      </div>

      <div className={previewOn ? 'note-page__split note-page__split--preview' : 'note-page__split'}>
        <div className="note-page__editor" ref={surfaceRef}>
          <Editor
            initialValue={handle.initialContent.body}
            onChange={handleBodyChange}
            onEditorReady={handleEditorReady}
            onRequestImage={onRequestImage}
            onRequestExtract={onRequestExtract}
            autoFocus={isNew}
            placeholder="ここから書きはじめられます"
            // 切り替えボタンはヘッダへ出す（エディタの上に行を作らない）。
            showSwitcher={false}
            onSwitcherChange={setEditorSwitcher}
          />
        </div>
        {previewOn && (
          <div className="note-page__preview" aria-label="プレビュー">
            <MarkdownView source={previewSource} />
          </div>
        )}
      </div>

      {/* 進行中の作業があるときだけ出す。常設の操作はすべてヘッダへ移した。 */}
      {(uploading > 0 || extracting) && (
        <p className="note-page__progress muted" role="status" aria-live="polite">
          {uploading > 0 && <span>画像を追加中…</span>}
          {extracting && <span>切り出し中…</span>}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          // 同じファイルを続けて選べるように値を戻す。
          event.target.value = '';
          for (const file of files) void uploadAndInsert(file);
        }}
      />

      {categories.error != null && <ErrorNotice error={categories.error} />}
    </div>
  );
}
